(() => {
  if (customElements.get('rosarium-native-extras')) return;
  const transactions = new Map();
  const validId = value => /^\d+$/.test(String(value)) && Number.isSafeInteger(Number(value)) && Number(value) > 0;
  const properties = data => Object.fromEntries([...data].filter(([key, value]) => /^properties\[.*\]$/.test(key) && typeof value === 'string' && value.trim()).map(([key, value]) => [key.slice(11, -1), value]));

  class RosariumNativeExtras extends HTMLElement {
    #controller;
    #busy = false;
    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      const options = {signal: this.#controller.signal};
      this.addEventListener('click', this.#click, options);
      const form = this.closest('form');
      form?.addEventListener('input', () => this.refresh(), options);
      form?.addEventListener('change', () => this.refresh(), options);
      form?.addEventListener('reset', () => queueMicrotask(() => {
        if (this.#busy) return;
        for (const button of this.querySelectorAll('[data-extra-choice]')) this.#set(button, button.dataset.extraKind === 'packaging' && !button.dataset.extraId ? 1 : 0);
        this.refresh();
      }), options);
      this.closest('.shopify-section')?.addEventListener('variant:update', event => {
        if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
        if (event.detail?.resource && Number.isFinite(Number(event.detail.resource.price))) this.dataset.basePrice = String(event.detail.resource.price);
        this.querySelector('[data-native-available]').value = String(Boolean(event.detail?.resource?.available));
        this.syncButton();
        this.refresh();
      }, options);
      this.dataset.ready = 'true';
      this.syncButton();
      this.refresh();
    }
    disconnectedCallback() { this.#controller?.abort(); }
    syncButton() {
      const button = this.closest('form')?.querySelector('[name="add"]');
      if (button) button.disabled = this.#busy || this.querySelector('[data-native-available]').value !== 'true';
    }
    get selection() { return [...this.querySelectorAll('[data-extra-choice][aria-pressed="true"]')].filter(x => !x.disabled); }
    #set(button, quantity) {
      const max = Number(button.dataset.extraMax || 99);
      if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > max) return;
      button.dataset.extraQuantity = String(quantity);
      button.setAttribute('aria-pressed', String(quantity > 0));
    }
    #click = event => {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled || this.#busy) return;
      if (button.dataset.extraCategory) {
        for (const tab of this.querySelectorAll('[data-extra-category]')) tab.setAttribute('aria-pressed', String(tab === button));
        for (const item of this.querySelectorAll('[data-extra-gifts] .rn-extras__item')) item.hidden = button.dataset.extraCategory !== 'all' && item.dataset.extraKind !== button.dataset.extraCategory;
        this.querySelector('[data-extra-gifts]').scrollLeft = 0;
      }
      if (button.hasAttribute('data-extra-choice')) {
        if (button.dataset.extraKind === 'packaging') {
          for (const wrap of this.querySelectorAll('[data-extra-choice][data-extra-kind="packaging"]')) this.#set(wrap, wrap === button ? 1 : 0);
        } else this.#set(button, button.getAttribute('aria-pressed') === 'true' ? 0 : 1);
      }
      if (button.dataset.extraAdjust) {
        const choice = button.closest('.rn-extras__item').querySelector('[data-extra-choice]');
        this.#set(choice, Number(choice.dataset.extraQuantity) + Number(button.dataset.extraAdjust));
        if (!Number(choice.dataset.extraQuantity)) choice.focus({preventScroll: true});
      }
      if (button.dataset.extraRemove) {
        const choice = [...this.querySelectorAll('[data-extra-choice]')].find(x => x.dataset.extraId === button.dataset.extraRemove);
        if (choice) {
          this.#set(choice, 0);
          if (choice.dataset.extraKind === 'packaging') this.#set(this.querySelector('[data-extra-choice][data-extra-kind="packaging"][data-extra-id=""]'), 1);
          const card = choice.closest('.rn-extras__item');
          if (card?.hidden) this.querySelector('[data-extra-category="all"]').click();
          choice.focus({preventScroll: true});
        }
      }
      this.refresh();
    };
    refresh() {
      if (this.#busy) return;
      const money = cents => new Intl.NumberFormat(this.dataset.locale, {style: 'currency', currency: this.dataset.currency}).format(cents / 100);
      const count = Math.max(1, Number(this.closest('form')?.querySelector('[name="quantity"]')?.value || 1));
      const selected = this.selection;
      let subtotal = 0;
      for (const choice of this.querySelectorAll('.rn-extras__item [data-extra-choice]')) {
        const qty = Number(choice.dataset.extraQuantity || 0);
        const stepper = choice.closest('.rn-extras__item').querySelector('[data-extra-stepper]');
        stepper.hidden = qty === 0;
        stepper.querySelector('[data-extra-count]').textContent = String(qty);
        stepper.querySelector('[data-extra-adjust="1"]').disabled = choice.disabled || qty >= Number(choice.dataset.extraMax || 99);
        stepper.querySelector('[data-extra-adjust="-1"]').disabled = choice.disabled;
        if (!choice.disabled) choice.querySelector('[data-extra-action]').textContent = qty ? this.dataset.addedLabel : this.dataset.addLabel;
      }
      const list = this.querySelector('[data-extra-selected]');
      list.replaceChildren();
      for (const choice of selected) {
        if (!choice.dataset.extraId) continue;
        const qty = choice.dataset.extraKind === 'packaging' ? count : Number(choice.dataset.extraQuantity);
        subtotal += Number(choice.dataset.extraPrice) * qty;
        const remove = document.createElement('button');
        remove.type = 'button'; remove.className = 'rn-extras__selected'; remove.dataset.extraRemove = choice.dataset.extraId;
        remove.setAttribute('aria-label', this.dataset.removeLabel.replace('__PRODUCT__', choice.dataset.extraTitle));
        const text = document.createElement('span'); text.textContent = `${qty} × ${choice.dataset.extraTitle}`;
        remove.append(text);
        remove.insertAdjacentHTML('beforeend', '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>');
        list.append(remove);
      }
      const message = this.querySelector('[data-extra-message]');
      if (message) {
        message.disabled = !selected.some(x => x.dataset.extraKind === 'cards');
        this.querySelector('[data-extra-message-panel]').hidden = message.disabled;
      }
      const base = Number(this.dataset.basePrice) * count;
      this.querySelector('[data-extra-base]').textContent = money(base);
      this.querySelector('[data-extra-subtotal]').textContent = money(subtotal);
      const total = this.querySelector('[data-extra-total]'); total.textContent = money(base + subtotal); total.dataset.amount = String(base + subtotal);
      this.dataset.subtotal = String(subtotal);
      this.dispatchEvent(new CustomEvent('native-extras:change', {bubbles: true, detail: {subtotal, total: base + subtotal}}));
    }
    static buildItems(formData, selection, labels, group = crypto.randomUUID()) {
      const id = Number(formData.get('id')), quantity = Number(formData.get('quantity') || 1);
      if (!validId(id) || !Number.isSafeInteger(quantity) || quantity < 1) throw Error('Invalid main variant or quantity');
      const parent = {id, quantity, properties: {...properties(formData), _rosarium_extras_group: group, _rosarium_extras_role: 'parent'}};
      const plan = formData.get('selling_plan');
      if (plan) { if (!validId(plan)) throw Error('Invalid selling plan'); parent.selling_plan = Number(plan); }
      const items = [parent], seen = new Set([id]);
      const wrappers = selection.filter(x => x.kind === 'packaging');
      if (wrappers.length > 1) throw Error('Multiple wrapping choices');
      if (wrappers[0]) parent.properties[labels.wrapping] = wrappers[0].title;
      for (const extra of selection) {
        if (!extra.id && extra.kind === 'packaging') continue;
        if (!validId(extra.id) || seen.has(Number(extra.id))) throw Error('Invalid or duplicate extra');
        const qty = extra.kind === 'packaging' ? quantity : Number(extra.quantity);
        if (!Number.isSafeInteger(qty) || qty < 1 || qty > Number(extra.maximum || 99)) throw Error('Invalid extra quantity');
        seen.add(Number(extra.id));
        const child = {id: Number(extra.id), quantity: qty, parent_id: id, properties: {_rosarium_extras_group: group, _rosarium_extras_role: extra.kind}};
        if (extra.kind === 'cards' && parent.properties[labels.message]) child.properties[labels.message] = parent.properties[labels.message];
        items.push(child);
      }
      return items;
    }
    static inspectItems(actual, expected) {
      if (!Array.isArray(actual)) return 'unknown';
      const group = expected[0].properties._rosarium_extras_group;
      const found = actual.filter(x => x.properties?._rosarium_extras_group === group);
      if (!found.length) return 'absent';
      if (found.length !== expected.length) return 'partial';
      const parent = found.find(x => x.properties?._rosarium_extras_role === 'parent');
      if (!parent?.key) return 'partial';
      for (const wanted of expected) {
        const got = found.find(x => Number(x.variant_id || x.id) === wanted.id);
        if (!got || Number(got.quantity) !== wanted.quantity) return 'partial';
        const clean = p => Object.entries(p || {}).filter(([, v]) => v !== '' && v != null);
        if (clean(got.properties).length !== clean(wanted.properties).length || clean(wanted.properties).some(([k, v]) => String(got.properties[k]) !== String(v))) return 'partial';
        if (wanted.parent_id && got.parent_relationship?.parent_key !== parent.key) return 'partial';
        if (String(got.selling_plan_allocation?.selling_plan?.id || '') !== String(wanted.selling_plan || '')) return 'partial';
      }
      return 'complete';
    }
    submitCartRequest({formData, url, sectionIds}) {
      const key = `${location.pathname}:${this.closest('form')?.id}:${this.dataset.productId}`;
      const previous = transactions.get(key);
      if (previous?.pending) return previous.pending;
      const selected = this.selection.map(x => ({id: x.dataset.extraId, title: x.dataset.extraTitle, kind: x.dataset.extraKind, quantity: x.dataset.extraQuantity, maximum: x.dataset.extraMax}));
      const fail = (review, response) => {
        this.querySelector('[data-extra-review]').hidden = !review;
        const description = review ? this.dataset.reviewError : response?.description || this.dataset.error;
        return {status: 422, message: description, description, nativeAddedItemCount: 0};
      };
      let items;
      try { items = RosariumNativeExtras.buildItems(formData, selected, {wrapping: this.dataset.wrapProperty, message: this.dataset.messageProperty}); }
      catch { return Promise.resolve(fail(false)); }
      if (this.dataset.preview === 'true') {
        const feedback = this.querySelector('[data-extra-feedback]'); feedback.hidden = false; feedback.textContent = this.dataset.previewMessage;
        this.dispatchEvent(new CustomEvent('native-extras:preview', {bubbles: true, detail: {items}}));
        return Promise.resolve({preview: true, items});
      }
      const transaction = previous || {items, pending: null};
      transactions.set(key, transaction);
      const controls = [...this.querySelectorAll('button,input,textarea')].map(button => ({button, disabled: button.disabled}));
      this.#busy = true; this.setAttribute('aria-busy', 'true');
      for (const {button} of controls) button.disabled = true;
      const sections = [...new Set(sectionIds)].slice(0, 5);
      const read = async () => { const r = await fetch(this.dataset.cartUrl + '.js', {headers: {Accept: 'application/json'}, cache: 'no-store', signal: AbortSignal.timeout(15000)}); if (!r.ok) throw Error('Cart read failed'); return r.json(); };
      const success = async response => {
        let rendered = response?.sections;
        if (!rendered && sections.length) {
          try { const target = new URL(location.href); target.searchParams.set('sections', sections.join(',')); const r = await fetch(target.pathname + target.search, {signal: AbortSignal.timeout(15000)}); if (r.ok) rendered = await r.json(); } catch {}
        }
        transactions.delete(key); this.querySelector('[data-extra-review]').hidden = true;
        return {items: response?.items || [], sections: rendered, nativeAddedItemCount: transaction.items.reduce((n, x) => n + x.quantity, 0)};
      };
      const run = async () => {
        if (previous) {
          try { const cart = await read(), state = RosariumNativeExtras.inspectItems(cart.items, previous.items); if (state === 'complete') return success(cart); if (state !== 'absent') return fail(true); transactions.delete(key); return fail(false); } catch { return fail(true); }
        }
        let response;
        try {
          const result = await fetch(url, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({items: transaction.items, sections, sections_url: location.pathname + location.search}), signal: AbortSignal.timeout(20000)});
          response = await result.json();
          if (result.ok && !response.status && RosariumNativeExtras.inspectItems(response.items, transaction.items) === 'complete') return success(response);
        } catch {}
        try { const cart = await read(), state = RosariumNativeExtras.inspectItems(cart.items, transaction.items); if (state === 'complete') return success(response); if (state === 'absent') { transactions.delete(key); return fail(false, response); } return fail(true); } catch { return fail(true); }
      };
      transaction.pending = Promise.resolve().then(run).finally(() => {
        transaction.pending = null; this.#busy = false; this.removeAttribute('aria-busy');
        for (const {button, disabled} of controls) button.disabled = disabled;
        this.refresh();
      });
      return transaction.pending;
    }
  }
  customElements.define('rosarium-native-extras', RosariumNativeExtras);
})();
